import { Anchor, CloseButton, Group, Text } from '@mantine/core';
import { experienceTier, formatDuration, type ShiftPlanning } from '@reditelji/shared';
import { useEffect } from 'react';
import { Link } from 'react-router';
import type { PoolVolunteer, TimelinePayload } from '../api/types';
import { plN } from '../lib/ui';
import { useUi } from '../state/ui';
import { Chips, Load, TIER_WORD } from './candidateUi';
import {
  briefingStale,
  dayStatusKey,
  lensEmptyReason,
  range,
  reasonChips,
  type LensFit,
} from './derive';
import { DraggableVolunteerCard } from './PoolCard';

/**
 * The Prostovoljci tab while the board looks through one person (SPEC §1.1B):
 * their day, and "Kam lahko" — every uncovered stretch they could take without
 * overruling anything, the one only they could take first. A row assigns the
 * piece it names, exactly as the fit drawn on the board does.
 */
export function LensPanel({
  data,
  person,
  fits,
  planning,
  silent,
  canAct,
  onAssign,
}: {
  data: TimelinePayload;
  person: PoolVolunteer;
  fits: LensFit[];
  planning: ShiftPlanning;
  silent: boolean;
  canAct: boolean;
  onAssign: (fit: LensFit) => void;
}) {
  const { setLens, setAssignPreview } = useUi();
  // The ghost belongs to the row under the pointer; nothing is left drawn once the list goes.
  useEffect(() => () => setAssignPreview(null), [setAssignPreview]);

  const mailed = person.briefingMailedVersion !== undefined;
  const reconfirms = !silent && mailed && !briefingStale(person);
  const empty = lensEmptyReason(data, planning, person);

  const preview = (f: LensFit | null) =>
    setAssignPreview(
      f && canAct
        ? {
            postId: f.post.id,
            fromMin: f.piece.fromMin,
            toMin: f.piece.toMin,
            volunteerId: person.id,
            name: person.name,
            tone: f.candidate.group === 'warn' ? 'warn' : 'rec',
          }
        : null,
    );

  return (
    <div className="tl-lens-panel">
      <DraggableVolunteerCard
        volunteer={person}
        data={data}
        dot={dayStatusKey(person, data)}
        size="lens"
        action={
          <CloseButton
            size="sm"
            aria-label="Končaj pogled na tablo"
            onClick={(e) => {
              e.stopPropagation();
              setLens(null);
            }}
          />
        }
      />
      <Group gap={8} wrap="wrap" className="tl-lens-sub">
        <span>
          Danes {person.dayMinutes > 0 ? formatDuration(person.dayMinutes) : '0 h'} ·{' '}
          {TIER_WORD[experienceTier(person.experience)]}
        </span>
        {reconfirms ? <span>✉ vsaka dodelitev gre v ponovno potrditev</span> : null}
        <Anchor component={Link} to={`/prostovoljci?volunteer=${person.id}`} size="xs">
          Profil
        </Anchor>
      </Group>

      <div className="tl-pool-group">
        <span className="tl-pool-group-dot" style={{ background: 'oklch(0.6 0.1 155)' }} />
        Kam lahko
        <span className="tl-cand-count">{fits.length}</span>
      </div>
      {fits.length === 0 ? (
        <Text size="xs" c="dimmed">
          {empty === 'declined'
            ? 'Ta dan ima zavrnjeno izmeno, zato ni med priporočenimi. Dodeliš jo lahko s klikom na vrzel.'
            : empty === 'full'
              ? `Ima že ${formatDuration(person.dayMinutes)}, dnevna meja je ${formatDuration(planning.maxDailyMin)}.`
              : 'Ta dan ni vrzeli, ki bi se ujela z razpoložljivostjo in izmenami te osebe.'}
        </Text>
      ) : (
        fits.map((f) => {
          const warns = reasonChips(f.ranked, data, planning, true).filter((c) => c.tone === 'warn');
          const recommended = (n: number) => plN(n, 'priporočen', 'priporočena', 'priporočeni', 'priporočenih');
          const body = (
            <>
              <span className="tl-lens-fit-top">
                <span className="tl-lens-fit-code">{f.post.code}</span>
                {f.post.urgent ? <span className="tl-urgent">NUJEN</span> : null}
                <span className="tl-lens-fit-time">{range(f.piece.fromMin, f.piece.toMin)}</span>
                <Load beforeMin={f.candidate.beforeMin} afterMin={f.candidate.afterMin} planning={planning} />
              </span>
              <span className="tl-lens-fit-micro">
                {f.post.micro}
                {f.narrows ? ` · od ${range(f.target.fromMin, f.target.toMin)}` : ''}
                {f.target.kind === 'open' ? ' · prosta izmena' : ''}
              </span>
              <Chips
                chips={[
                  f.only
                    ? { tone: 'mail', text: 'edini priporočeni' }
                    : f.recCount === 0
                      ? { tone: 'info', text: 'nihče ni priporočen' }
                      : f.candidate.group === 'rec'
                        ? { tone: 'info', text: `še ${recommended(f.recCount - 1)}` }
                        : { tone: 'info', text: recommended(f.recCount) },
                  ...warns,
                ]}
              />
            </>
          );
          // Two open rows at a post may start together; a row is its own id.
          const key = f.target.kind === 'open' ? f.target.shiftId : `${f.post.id}:gap-${f.target.fromMin}`;
          return canAct ? (
            <button
              key={key}
              type="button"
              className={`tl-lens-fit${f.candidate.group === 'warn' ? ' tl-lens-fit--warn' : ''}${f.only ? ' tl-lens-fit--only' : ''}`}
              onMouseEnter={() => preview(f)}
              onMouseLeave={() => preview(null)}
              onFocus={() => preview(f)}
              onBlur={() => preview(null)}
              onClick={() => {
                preview(null);
                onAssign(f);
              }}
              title={`Dodeli ${person.name}: ${f.post.code} ${range(f.piece.fromMin, f.piece.toMin)}`}
            >
              {body}
            </button>
          ) : (
            <div key={key} className="tl-lens-fit tl-lens-fit--static">
              {body}
            </div>
          );
        })
      )}
    </div>
  );
}
